import { useCallback, useEffect, useRef, useState } from 'react';
import { WSClient } from '@/lib/ws-client';
import type { ContinuousAuthEvent, ContinuousAuthStatus } from '@/types/auth';

interface UseWebSocketOptions {
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** WebSocket endpoint */
  endpoint?: string;
}

interface UseWebSocketReturn {
  /** Current connection status */
  status: ContinuousAuthStatus;
  /** Latest auth event */
  lastEvent: ContinuousAuthEvent | null;
  /** Current score (from last score_update event) */
  currentScore: number | null;
  /** Connect to WebSocket */
  connect: () => void;
  /** Disconnect from WebSocket */
  disconnect: () => void;
  /** Send keystroke events for evaluation */
  sendEvents: (events: unknown) => void;
  /** Event history */
  eventHistory: ContinuousAuthEvent[];
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { autoConnect = false, endpoint } = options;
  const clientRef = useRef<WSClient>(
    new WSClient(endpoint ? { endpoint } : undefined),
  );

  const [status, setStatus] = useState<ContinuousAuthStatus>('disconnected');
  const [lastEvent, setLastEvent] = useState<ContinuousAuthEvent | null>(null);
  const [currentScore, setCurrentScore] = useState<number | null>(null);
  const [eventHistory, setEventHistory] = useState<ContinuousAuthEvent[]>([]);

  useEffect(() => {
    const client = clientRef.current;

    const unsubStatus = client.onStatus((newStatus) => {
      setStatus(newStatus);
    });

    const unsubEvent = client.onEvent((event) => {
      setLastEvent(event);
      setEventHistory((prev) => [...prev.slice(-99), event]);

      if (event.type === 'score_update' && event.score !== undefined) {
        setCurrentScore(event.score);
      }
    });

    if (autoConnect) {
      client.connect();
    }

    return () => {
      unsubStatus();
      unsubEvent();
      client.destroy();
    };
  }, [autoConnect]);

  const connect = useCallback(() => {
    clientRef.current.connect();
  }, []);

  const disconnect = useCallback(() => {
    clientRef.current.disconnect();
  }, []);

  const sendEvents = useCallback((events: unknown) => {
    clientRef.current.sendKeystrokeEvents(events);
  }, []);

  return {
    status,
    lastEvent,
    currentScore,
    connect,
    disconnect,
    sendEvents,
    eventHistory,
  };
}
